import { GoalImpactLevel } from '@griply/types';
import { schemeColor } from 'shared/assets/styles/scheme-color.style';
import { BlurHashImage } from 'shared/components/connected/blur-hash-image';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, DefaultTheme } from 'styled-components';

export enum DataTypes {
  Title = 'title',
  StartDate = 'start-date',
  Deadline = 'deadline',
  Progress = 'progress',
  Tasks = 'tasks',
  Impact = 'impact',
  LifeArea = 'life-area',
  Interactions = 'interactions',
}

const dataTypeStyles = (type: DataTypes, theme: DefaultTheme) => {
  switch (type) {
    case DataTypes.Title:
      return css`
        flex: 1;
        min-width: 0;
      `;
    case DataTypes.Progress:
      return css`
        width: 8rem;
        color: ${theme.palette.primaryExtended.opacityBackgroundText};
      `;
    case DataTypes.Impact:
      return css`
        width: 6.4rem;
        display: none;

        @container goal-list (min-width: 60rem) {
          display: flex;
        }
      `;
    case DataTypes.StartDate:
      return css`
        width: 8.8rem;
        display: none;

        @container goal-list (min-width: 80rem) {
          display: flex;
        }
      `;
    case DataTypes.Deadline:
      return css`
        width: 8rem;
      `;
    case DataTypes.Tasks:
      return css`
        width: 5.6rem;
        display: none;

        @container goal-list (min-width: 70rem) {
          display: flex;
        }
      `;
    case DataTypes.LifeArea:
      return css`
        width: 16.6rem;
        display: none;

        @container goal-list (min-width: 95rem) {
          display: flex;
        }
      `;
    case DataTypes.Interactions:
      return css`
        width: 3.4rem;
        color: ${theme.palette.grey[500]};
      `;
  }
};

export const Container = styled.div<{
  $isArchived: boolean;
  $isSelected?: boolean;
  $isDropTarget?: boolean;
}>`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};

  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => `0 0 0 ${theme.spacing(4)}`};

  ${({ $isArchived }) =>
    $isArchived &&
    css`
      opacity: 0.5;
    `}

  ${({ $isDropTarget, theme }) =>
    $isDropTarget &&
    css`
      background: ${theme.palette.grey[100]} !important;
    `}

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      background: ${theme.palette.primaryExtended.opacityBackgroundBackground};
    `}
`;

export const RightGroup = styled.span`
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: space-between;
`;

export const DataContainer = styled.span<{
  $type: DataTypes;
  $isFrozen: boolean;
  $isClickable?: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: flex;
  align-items: center;
  gap: ${({ theme, $type }) =>
    theme.spacing($type === DataTypes.Title ? 2 : 1)};
  height: 4.8rem;

  ${({ $type, theme }) => dataTypeStyles($type, theme)};

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
    `};

  &:hover ${() => SelectPlaceholder} {
    display: inline;
  }

  ${({ $isFrozen, theme }) =>
    $isFrozen &&
    css`
      color: ${theme.palette.text.secondary};
      pointer-events: none;
    `};
`;

export const IllustrationContainer = styled.span<{ $indentLevel?: number }>`
  position: relative;
  display: block;
  // padding-left: ${({ theme }) => theme.spacing(0.5)};

  ${({ $indentLevel, theme }) =>
    !!$indentLevel &&
    css`
      // Indent the goal name by depth via a margin on a flex child, not padding
      // on the flex title column — a child margin is absorbed by the shrinking
      // name column, so the title's outer width (and the right columns) stay
      // put, while a long name still truncates within the reduced space.
      margin-left: calc(${$indentLevel} * ${theme.spacing(2.5)});
    `};
`;

export const ImageContainer = styled.span<{
  $iconColor?: string;
  $transparent?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 2rem;
  width: 2rem;
  min-width: 2rem;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ $transparent, theme }) =>
    $transparent ? 'transparent' : theme.palette.grey[100]};
  overflow: hidden;

  font-size: 1.4rem;
  line-height: 62.5%;
  color: ${({ $iconColor, theme }) => $iconColor ?? theme.palette.grey[700]};
`;

export const Image = styled(BlurHashImage)`
  display: block;
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

export const CompletedContainer = styled.span`
  position: absolute;
  top: 0;
  right: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 1.2rem;
  height: 1.2rem;

  transform: translate(33%, -33%);
  background: ${({ theme }) => theme.palette.success.main};
  border-radius: 50%;

  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.success.contrastText};
`;

export const ColorDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: ${({ theme }) => `-${theme.spacing(0.75)}`};
  right: ${({ theme }) => `-${theme.spacing(0.75)}`};

  display: block;
  height: 1rem;
  width: 1rem;

  border: ${({ theme }) =>
    `${theme.spacing(0.5)} solid ${theme.palette.background.default};`};
  border-radius: 50%;
  background: ${({ $color, theme }) => schemeColor($color, theme)};
`;

export const TitleColumn = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

export const Title = styled.span<{ $isCompleted: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.subtitle2)};

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $isCompleted }) =>
    $isCompleted &&
    css`
      text-decoration: line-through;
    `};
`;

export const ParentLabels = styled.span`
  display: flex;
  align-self: flex-start;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
`;

export const ParentSeparator = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  flex: none;
  white-space: pre;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ParentGoalLabel = styled.button`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;

  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration: underline;
  }
`;

export const TaskCount = styled.span<{ $isEmpty: boolean }>`
  ${({ $isEmpty, theme }) =>
    $isEmpty &&
    css`
      color: ${theme.palette.text.secondary};
    `};
`;

export const SelectPlaceholder = styled.span<{ $forceShow?: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  display: ${({ $forceShow }) => ($forceShow ? 'inline' : 'none')};
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  margin-left: ${({ theme }) => theme.spacing(-1.5)};
`;

export const DataButton = styled.span<{
  $isSmallFont?: boolean;
  $background?: string;
  $color?: string;
  $static?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
  max-width: 100%;
  margin-left: ${({ theme }) => theme.spacing(-1.5)};

  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.spacing(2)};

  ${({ $static, theme }) =>
    !$static &&
    css`
      &:hover {
        background: ${theme.palette.grey[100]};
      }
    `};

  ${({ $isSmallFont, theme }) =>
    $isSmallFont &&
    css`
      ${typographyToCss(theme.typography.buttonSmall)};
      padding: ${({ theme }) => `${theme.spacing(0.75)} ${theme.spacing(1.5)}`};
    `};

  ${({ $background }) =>
    $background &&
    css`
      background: ${$background};
      border-color: ${$background};
    `};

  ${({ $color }) =>
    $color &&
    css`
      color: ${$color};
    `};
`;

export const ImpactIcons = styled.span<{ $impact: GoalImpactLevel }>`
  display: inline-flex;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing(0.5)};
  padding-bottom: ${({ theme }) => theme.spacing(0.5)};

  transition: color 200ms;
  color: ${({ theme, $impact }) => theme.palette.goalImpact[$impact].text};

  & > * + * {
    margin-left: ${({ theme }) => theme.spacing(-0.75)};
  }
`;

export const OverflowSpan = styled.span`
  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const IconContainer = styled.span`
  font-size: 1.6rem;
  line-height: 62.5%;
`;

export const MetricButton = styled.button<{ $static?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
  max-width: 100%;
  padding: 0;

  background: none;
  border: none;
  text-align: left;
  color: inherit;

  ${({ $static }) =>
    !$static &&
    css`
      cursor: pointer;

      &:hover ${() => MetricValue} {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    `};
`;

export const MetricChartContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.8rem;
`;

export const MetricValue = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: flex;
  align-items: baseline;
  min-width: 0;

  overflow: hidden;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.primary};
`;
